// Server-time-based game status.
// The customer app must NEVER decide open/close using the device clock.
// All timing decisions come from Postgres (clock_timestamp() in Asia/Kolkata)
// via the get_game_server_status / get_games_server_status RPCs.

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ServerGameStatus = {
  game_id: string;
  is_betting_open: boolean;
  status: "OPEN" | "CLOSED" | "UPCOMING";
  open_time?: string | null;
  close_time?: string | null;
  result_time?: string | null;
};

export type ServerGamesStatus = {
  server_time_ist: string;
  games: ServerGameStatus[];
};

/** Bulk status for the home page / matches list. Server clock only. */
export function useServerGamesStatus() {
  const q = useQuery<ServerGamesStatus>({
    queryKey: ["server_games_status"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_games_server_status");
      if (error) throw error;
      return data as ServerGamesStatus;
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
  return q;
}

/** Per-game status for the play page. Server clock only. */
export function useServerGameStatus(gameId: string | undefined) {
  const q = useQuery<ServerGameStatus & { server_time_ist: string; ok: boolean }>({
    queryKey: ["server_game_status", gameId],
    enabled: !!gameId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_game_server_status", { _game_id: gameId! });
      if (error) throw error;
      return data as any;
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });

  // When the tab/app returns from background, force an immediate refresh.
  useEffect(() => {
    function onVisible() { if (document.visibilityState === "visible") q.refetch(); }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [q]);

  return q;
}

export function statusLabel(status?: string): string {
  if (status === "UPCOMING") return "Betting has not started yet.";
  if (status === "CLOSED") return "Betting Closed.";
  return "";
}

export function pickStatus(bulk: ServerGamesStatus | undefined, gameId: string): ServerGameStatus | undefined {
  return bulk?.games?.find((g) => g.game_id === gameId);
}
